import React, { useRef, useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, Reveal, FadeIn } from "../components/site/Reveal";
import { JourneyRouteMap } from "../components/site/JourneyMap";
import { JOURNEYS, JOURNEY_THEMES } from "../data/content";
import { fetchJourney } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

const Hero = ({ j, heroSrc }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  return (
    <section ref={ref} className="relative min-h-[80vh] flex items-end overflow-hidden bg-ink">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img src={heroSrc} alt={j.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/40" />
      </motion.div>
      <div className="relative yadid-container pb-16 md:pb-24 pt-40">
        <FadeIn delay={0.15}>
          <Link
            to="/journeys"
            className="link-underline eyebrow text-brass inline-flex items-center gap-2 mb-10"
          >
            <ArrowLeft size={14} /> All Journeys
          </Link>
        </FadeIn>
        <MaskedLines
          tag="h1"
          lines={[j.name]}
          delay={0.25}
          className="font-serif text-ivory text-4xl sm:text-6xl lg:text-[4.75rem] leading-[0.98] tracking-[-0.03em]"
        />
        <Reveal delay={0.55} className="mt-8 max-w-2xl">
          <p className="font-serif text-2xl md:text-3xl text-brass italic">{j.tagline}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default function JourneyDetail() {
  const { slug } = useParams();
  const j = JOURNEYS[slug];
  const [override, setOverride] = useState(null);

  useEffect(() => {
    let active = true;
    if (JOURNEYS[slug]) {
      fetchJourney(slug)
        .then((d) => active && setOverride(d))
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [slug]);

  if (!j) return <Navigate to="/journeys" replace />;

  const heroSrc = override && override.hero ? override.hero : j.hero;
  const gallery = override && override.gallery && override.gallery.length ? override.gallery : j.gallery;

  const others = JOURNEY_THEMES.filter((t) => t.slug !== slug);

  return (
    <div data-testid="journey-detail-page" className="bg-ink text-ivory">
      <Hero j={j} heroSrc={heroSrc} />

      {/* Destinations map — leads the story */}
      <JourneyRouteMap slug={slug} />

      {/* The question + overview */}
      <section className="yadid-container py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow text-brass mb-8">The Question</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.15] tracking-[-0.02em] text-ivory italic">
                {j.question}
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <div className="space-y-6 text-lg text-ivory/70 font-light leading-relaxed">
                {j.overview.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="border-y border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {gallery.map((src, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="relative h-64 md:h-80 overflow-hidden bg-ink">
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* The arc / itinerary */}
      <section className="yadid-container py-24 md:py-36">
        <Reveal>
          <p className="eyebrow text-brass mb-6">The Arc of the Journey</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory tracking-[-0.02em] max-w-3xl">
            How the journey unfolds.
          </h2>
        </Reveal>
        <div className="mt-16 border-t border-white/10">
          {j.arc.map((step, i) => (
            <Reveal key={i} delay={(i % 3) * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-white/10">
                <div className="md:col-span-1">
                  <span className="font-serif text-brass text-2xl">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif text-2xl md:text-3xl text-ivory">{step.title}</h3>
                </div>
                <div className="md:col-span-6 md:col-start-6">
                  <p className="text-ivory/65 font-light leading-relaxed">{step.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Reflection */}
      <section className="border-t border-white/10">
        <div className="yadid-container py-24 md:py-32">
          <Reveal>
            <p className="eyebrow text-brass mb-10">On Return</p>
            <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.2] tracking-[-0.02em] text-ivory max-w-4xl italic border-l-2 border-brass pl-8 md:pl-12">
              {j.reflection}
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* CTA + other journeys */}
      <section className="border-t border-white/10">
        <div className="yadid-container py-24 md:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
            <div className="max-w-2xl">
              <Reveal>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ivory tracking-[-0.02em] leading-[1.05]">
                  Design this journey with us.
                </h2>
                <p className="mt-6 text-ivory/60 font-light leading-relaxed max-w-lg">
                  Every journey is shaped around the people participating and the understanding they
                  hope to develop.
                </p>
                <div className="mt-10">
                  <Link
                    to="/contact"
                    data-testid="journey-detail-cta"
                    className="inline-flex items-center gap-3 border border-brass text-brass px-8 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 hover:bg-brass hover:text-ink"
                  >
                    Begin a Conversation About a Journey <ArrowRight size={15} />
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:w-72">
              <p className="eyebrow text-brass mb-6">Other Journeys</p>
              <ul className="space-y-3">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link
                      to={`/journeys/${o.slug}`}
                      className="font-serif text-2xl text-ivory/80 hover:text-brass transition-colors duration-400 link-underline"
                    >
                      {o.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
